// src/evaluations/AssessmentResultViewSimple.js — resultados del assessment en modo simple
// (PLAN_UNIFICACION_ASSESSMENT.md): sin score psicolaboral ni NineBox. Traído de
// src/assessment2/AssessmentResultView.js, que sigue existiendo hasta la limpieza del módulo.
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
    getCompetencyScore,
    getAnsweredQuestionsCount,
    getTotalQuestionsCount
} from './competencyScoreUtils';

const AssessmentResultViewSimple = ({ data }) => {
    const { t } = useTranslation();

    if (!data) return null;

    // Helper para obtener el color según el score
    const getScoreColor = (score) => {
        if (score >= 70) return "success";
        if (score >= 50) return "warning";
        return "error";
    };

    // Helper para convertir timestamp de Firestore a Date
    const fsTsToDate = (ts) => {
        if (!ts) return null;
        if (ts._seconds) return new Date(ts._seconds * 1000);
        if (ts.seconds) return new Date(ts.seconds * 1000);
        return new Date(ts);
    };

    const overallScore = data.overallScore || 0;
    const confidenceIndex = data.confidenceIndex || 0;

    const plan = data.plan_desarrollo || {};

    const plan90 = plan.dias_90 || plan.primeros_90_dias;
    const plan180 = plan.dias_180 || plan.siguientes_90_dias;
    const plan360 = plan.dias_360 || plan.tercer_periodo;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header Card */}
            <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f8fafc' }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.candidate')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {data.applicantName || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.position')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {data.positionName || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.completedDate')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {data.completedAt
                                    ? fsTsToDate(data.completedAt)?.toLocaleString('es-ES')
                                    : 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.confidenceIndex')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {confidenceIndex}%
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Scores principales (modo simple: solo score del assessment, sin psicolaboral) */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('evaluationResponses.scoreAssessment')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h2" fontWeight={700} color={`${getScoreColor(overallScore)}.main`}>
                                    {overallScore}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    / 100
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={overallScore}
                                color={getScoreColor(overallScore)}
                                sx={{ height: 10, borderRadius: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

            </Grid>

            {/* Competencias evaluadas con fortalezas y áreas de mejora */}
            {data.capabilities && data.capabilities.length > 0 && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon />
                            {t('evaluationResponses.evaluatedCompetencies')}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {data.capabilities.map((capability, capIdx) => {
                            const competencyScore = getCompetencyScore(data, capability);
                            const answeredCount = getAnsweredQuestionsCount(capability);
                            const totalCount = getTotalQuestionsCount(capability);

                            // Extraer fortalezas y áreas de mejora de esta competencia
                            const strengths = [];
                            const improvements = [];

                            capability.questions?.forEach(q => {
                                if (q.strengthsForPosition) strengths.push(...q.strengthsForPosition);
                                if (q.improvementAreas) improvements.push(...q.improvementAreas);
                            });

                            const uniqueStrengths = [...new Set(strengths)];
                            const uniqueImprovements = [...new Set(improvements)];

                            return (
                                <Box key={capIdx} sx={{ mb: 4, '&:last-child': { mb: 0 } }}>
                                    {/* Header de la competencia */}
                                    <Box sx={{
                                        p: 2,
                                        bgcolor: '#f8fafc',
                                        borderRadius: 1,
                                        mb: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Typography variant="h6" fontWeight={600}>
                                            {capability.capabilityName}
                                        </Typography>
                                        <Chip
                                            label={`${competencyScore}/100`}
                                            color={getScoreColor(competencyScore)}
                                            size="medium"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        color={answeredCount < totalCount ? 'warning.main' : 'text.secondary'}
                                        sx={{ mb: 2 }}
                                    >
                                        {t('evaluationResponses.questionsAnswered', {
                                            answered: answeredCount,
                                            total: totalCount
                                        })}
                                    </Typography>

                                    {/* Fortalezas y Áreas de Mejora de esta competencia */}
                                    <Grid container spacing={2}>
                                        {uniqueStrengths.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom>
                                                    {t('evaluationResponses.strengthsForPosition')}
                                                </Typography>
                                                <List dense>
                                                    {uniqueStrengths.map((strength, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon color="success" fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={strength}
                                                                primaryTypographyProps={{
                                                                    variant: "body2",
                                                                    color: "text.secondary"
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Grid>
                                        )}

                                        {uniqueImprovements.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight={600} color="warning.main" gutterBottom>
                                                    {t('evaluationResponses.improvementAreas')}
                                                </Typography>
                                                <List dense>
                                                    {uniqueImprovements.map((improvement, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <WarningIcon color="warning" fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={improvement}
                                                                primaryTypographyProps={{
                                                                    variant: "body2",
                                                                    color: "text.secondary"
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Grid>
                                        )}
                                    </Grid>

                                    {capIdx < data.capabilities.length - 1 && <Divider sx={{ mt: 3 }} />}
                                </Box>
                            );
                        })}
                    </CardContent>
                </Card>
            )}

            {/* Plan de Desarrollo */}
            {data.plan_desarrollo && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('evaluationResponses.developmentPlan90180360')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {plan.introduccion && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                                {plan.introduccion}
                            </Typography>
                        )}

                        <Grid container spacing={2}>
                            {plan90 && (
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                                        <Typography variant="subtitle1" fontWeight={600} color="primary.main" gutterBottom>
                                            {t('evaluationResponses.first90Days')}
                                        </Typography>

                                        {plan90.objetivos?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <List dense>
                                                    {plan90.objetivos.map((obj, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <TrendingUpIcon fontSize="small" color="primary" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={obj} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan90.acciones?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <List dense>
                                                    {plan90.acciones.map((acc, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={acc} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan90.indicadores?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <List dense>
                                                    {plan90.indicadores.map((ind, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={ind} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {plan180 && (
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                                        <Typography variant="subtitle1" fontWeight={600} color="secondary.main" gutterBottom>
                                            {t('evaluationResponses.next90Days')}
                                        </Typography>

                                        {plan180.objetivos?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <List dense>
                                                    {plan180.objetivos.map((obj, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <TrendingUpIcon fontSize="small" color="secondary" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={obj} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan180.acciones?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <List dense>
                                                    {plan180.acciones.map((acc, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={acc} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan180.indicadores?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <List dense>
                                                    {plan180.indicadores.map((ind, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={ind} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {plan360 && (
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                                        <Typography variant="subtitle1" fontWeight={600} color="success.main" gutterBottom>
                                            {t('evaluationResponses.thirdPeriod')}
                                        </Typography>

                                        {plan360.objetivos?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <List dense>
                                                    {plan360.objetivos.map((obj, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <TrendingUpIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={obj} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan360.acciones?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <List dense>
                                                    {plan360.acciones.map((acc, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={acc} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {plan360.indicadores?.length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <List dense>
                                                    {plan360.indicadores.map((ind, idx) => (
                                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={ind} primaryTypographyProps={{ variant: "body2" }} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Conclusión Integrada */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('evaluationResponses.integratedConclusion')}
                    </Typography>

                    {data.conclusion_integrada ? (
                        typeof data.conclusion_integrada === 'string' ? (
                            // Formato antiguo (string con párrafos)
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                                {data.conclusion_integrada}
                            </Typography>
                        ) : (
                            // Formato nuevo (objeto estructurado)
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {data.conclusion_integrada.vision_general && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.generalPerformanceView')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.vision_general}
                                        </Typography>
                                    </Box>
                                )}

                                {data.conclusion_integrada.oportunidades_desarrollo && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.developmentOpportunities')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.oportunidades_desarrollo}
                                        </Typography>
                                    </Box>
                                )}

                                {data.conclusion_integrada.sintesis_perfil && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.profileSynthesis')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.sintesis_perfil}
                                        </Typography>
                                    </Box>
                                )}

                                {data.conclusion_integrada.implicaciones_cargo && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {t('evaluationResponses.positionImplications')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {data.conclusion_integrada.implicaciones_cargo}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {t('evaluationResponses.noConclusionAvailable')}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default AssessmentResultViewSimple;